<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { ComponentPublicInstance, CSSProperties } from 'vue'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import Icon from '@/components/Icon.vue'

type PopoverPanelPosition =
  | 'bottom-left'
  | 'bottom-right'
  | 'top-left'
  | 'top-right'

const props = defineProps({
  ariaLabel: {
    type: String,
    default: null,
  },
  buttonClass: {
    type: [String, Array, Object],
    default: null,
  },
  buttonLabel: {
    type: String,
    default: null,
  },
  customButton: {
    type: Boolean,
    default: false,
  },
  fitViewport: {
    type: Boolean,
    default: false,
  },
  panelClass: {
    type: [String, Array, Object],
    default: null,
  },
  position: {
    type: String as () => PopoverPanelPosition,
    default: 'bottom-right',
  },
})

const button = ref<ComponentPublicInstance | null>(null)
const panel = ref<ComponentPublicInstance | null>(null)
const panelStyle = ref<CSSProperties>({})

watch(
  [() => panel.value?.$el, () => props.fitViewport, () => props.position],
  ([panelElement, fitViewport], _, onCleanup) => {
    panelStyle.value = {}

    const buttonElement = button.value?.$el

    if (
      !fitViewport ||
      !(panelElement instanceof HTMLElement) ||
      !(buttonElement instanceof HTMLElement)
    ) {
      return
    }

    const updatePosition = () => {
      const margin = 16
      const gap = 8
      const viewportWidth = document.documentElement.clientWidth || innerWidth
      const viewportHeight = innerHeight
      const anchor = buttonElement.getBoundingClientRect()
      const availableAbove = Math.max(0, anchor.top - margin - gap)
      const availableBelow = Math.max(
        0,
        viewportHeight - anchor.bottom - margin - gap
      )
      const contentHeight = panelElement.scrollHeight
      const preferAbove = props.position.startsWith('top')
      const above = preferAbove
        ? availableAbove >= contentHeight || availableAbove > availableBelow
        : availableBelow < contentHeight && availableAbove > availableBelow
      const availableHeight = Math.min(
        above ? availableAbove : availableBelow,
        Math.max(0, viewportHeight - margin * 2)
      )
      const height = Math.min(contentHeight, availableHeight)
      const width = Math.min(
        panelElement.offsetWidth,
        viewportWidth - margin * 2
      )
      const preferredLeft = props.position.endsWith('right')
        ? anchor.right - width
        : anchor.left
      const left = Math.max(
        margin,
        Math.min(preferredLeft, viewportWidth - width - margin)
      )
      const top = Math.max(
        margin,
        Math.min(
          above ? anchor.top - gap - height : anchor.bottom + gap,
          viewportHeight - height - margin
        )
      )

      panelStyle.value = {
        top: `${top}px`,
        left: `${left}px`,
        maxWidth: `${Math.max(0, viewportWidth - margin * 2)}px`,
        maxHeight: `${availableHeight}px`,
        overflowY: 'auto',
        transform: 'none',
      }
    }

    updatePosition()

    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updatePosition)
        : null

    observer?.observe(buttonElement)
    observer?.observe(panelElement)
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    onCleanup(() => {
      observer?.disconnect()
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    })
  },
  { flush: 'post' }
)

const positionClass = computed(() => {
  if (['top', 'right'].every((position) => props.position.includes(position))) {
    return 'tw-right-0 tw-origin-bottom-right'
  }
  if (
    ['bottom', 'left'].every((position) => props.position.includes(position))
  ) {
    return 'tw-left-0 tw-origin-top-left'
  }
  if (['top', 'left'].every((position) => props.position.includes(position))) {
    return 'tw-left-0 tw-top-0 tw--translate-y-full tw-origin-bottom-left'
  }

  return 'tw-right-0 tw-origin-top-right'
})
</script>

<template>
  <Popover
    v-slot="{ open: popoverOpen }"
    class="tw-relative"
  >
    <PopoverButton
      ref="button"
      v-slot="{ open }"
      :aria-label="props.ariaLabel"
      :title="props.ariaLabel"
      :as="props.customButton ? 'div' : 'button'"
      class="tw-text-grey-medium hover:tw-text-primary focus:tw-text-primary"
      :class="buttonClass"
    >
      <slot v-bind="{ open }">
        <div
          class="tw-flex tw-w-full tw-max-w-full tw-cursor-pointer tw-select-none tw-items-center tw-gap-x-1.5 tw-text-xs"
        >
          <div class="tw-truncate tw-font-medium">
            {{ props.buttonLabel }}
          </div>
          <Icon
            name="chevron-down"
            class="tw-text-2xs"
          />
        </div>
      </slot>
    </PopoverButton>

    <Teleport
      to="body"
      :disabled="!props.fitViewport"
    >
      <transition
        enter-active-class="tw-transition tw-duration-200 tw-ease-out"
        enter-from-class="tw-translate-y-1 tw-opacity-0"
        enter-to-class="tw-translate-y-0 tw-opacity-100"
        leave-active-class="tw-transition tw-duration-150 tw-ease-in"
        leave-from-class="tw-translate-y-0 tw-opacity-100"
        leave-to-class="tw-translate-y-1 tw-opacity-0"
      >
        <PopoverPanel
          v-if="!props.fitViewport || popoverOpen"
          ref="panel"
          v-slot="{ close, open }"
          class="tw-z-50 tw-rounded-md tw-bg-white tw-shadow-lg tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none"
          :class="[
            props.panelClass,
            props.fitViewport
              ? 'tw-fixed'
              : ['tw-absolute tw-transform', positionClass],
          ]"
          :style="panelStyle"
        >
          <slot
            name="popover"
            v-bind="{ close, open }"
          />
        </PopoverPanel>
      </transition>
    </Teleport>
  </Popover>
</template>
